import { Popover, Table, Tooltip, Col } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerButton from '../../../../components/custom_components/Button'
import { PerEmpty } from '../../../../components/custom_components/Empty'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerIcon from '../../../../components/custom_components/Icon'
import { tableActionButtons } from '../../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTag } from '../../../../components/custom_components/Tag'
import PerText from '../../../../components/custom_components/Text'
import { useAuth } from '../../../../context/auth-context'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../../shared/useDateTime'
import { atobUtf8, useObjectiveAttachmentHelper } from '../../../../shared/useObjectiveAttachmentHelper'
import { KeyResultDto, ObjectiveStatus, ObjectiveDto } from '../../../../types'
import { createObjectivesActionButtons } from './ActionButton'
import { ObjectiveColumnKey } from './objectiveColumnKey'
import { IconIndicator } from '../../../../components/custom_components/IconIndicator/IconIndicator'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../../context/currency-context'

export enum ObjectiveListType {
  Monetary = 'monetary',
  PastMonetary = 'pastMonetary',
  Development = 'development',
  PastDevelopment = 'pastDevelopment',
  Suggestion = 'suggestion',
}

interface Props {
  objectives: ObjectiveDto[]
  handleObjectiveClicked: (isManagerPreparation: boolean, objective: ObjectiveDto | undefined) => void
  handleObjectiveDismiss?: (objectiveId: number) => Promise<void>
  handleObjectiveDelete?: (objective: ObjectiveDto | undefined) => void
  handleObjectiveReactivate?: (objectiveId: number) => Promise<void>
  handleObjectiveDuplicate?: (objectiveGlobalId: string) => Promise<void>
  employeeView?: boolean
  isManagerPreparation?: boolean
  employeeFirstName?: string
  listType: ObjectiveListType
  toBePaid?: boolean
  disabled?: boolean
  light?: boolean
  showTasks?: boolean
  showStartDate?: boolean
  hideAction?: boolean
  showStatus?: boolean
  showToBePaidAndBonus?: boolean
  moneyEditable?: boolean
  fullWidth?: boolean
  canDuplicate?: boolean
  handleObjectiveDuplicateAndDismissOrDelete?: (objective: ObjectiveDto | undefined) => void
}

export const ObjectiveList: FC<Props> = ({
  objectives,
  handleObjectiveClicked,
  handleObjectiveDismiss,
  handleObjectiveDelete,
  handleObjectiveReactivate,
  handleObjectiveDuplicate,
  employeeView = false,
  isManagerPreparation,
  employeeFirstName,
  listType,
  toBePaid,
  disabled = false,
  showTasks,
  showStartDate,
  hideAction,
  showStatus,
  showToBePaidAndBonus,
  fullWidth,
  canDuplicate = false,
  handleObjectiveDuplicateAndDismissOrDelete = undefined,
}) => {
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const [t] = useTranslation()

  const { currency: employeeCurrentContractCurrency, currencyConversionData } = useCurrency()

  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { extractHumanReadableFileName } = useObjectiveAttachmentHelper(atobUtf8)

  const [isDismissing, toggleDismissing] = useState<boolean>(false)
  const [isReactivating, toggleReactivating] = useState<boolean>(false)
  const [isDuplicating, toggleDuplicating] = useState<boolean>(false)

  const monetary = [ObjectiveListType.Monetary, ObjectiveListType.PastMonetary].includes(listType)
  const development = [ObjectiveListType.Development, ObjectiveListType.PastDevelopment].includes(listType)

  const generateKeyResults = (keyResults: KeyResultDto[]) =>
    keyResults.map((kr: KeyResultDto, index: number) => (
      <PerFlexRow align="middle" mb="xxxs" key={index} noWrap>
        <div style={{ width: '20px', textAlign: 'center' }} className="per-flexRow--fixed">
          <PerIcon
            name={kr.completed ? 'check_stroke' : 'circle_fill'}
            color={kr.completed ? 'primary' : 'secondary'}
            size={kr.completed ? 18 : 8}
          />
        </div>
        <PerText color={kr.completed ? 'default' : 'secondary'} ml="xxs" className="per-flexRow--growShrink">
          {kr.text}
        </PerText>
      </PerFlexRow>
    ))

  const generateTitle = (isMonetary: boolean, isDevelopment: boolean): string => {
    if (isMonetary) {
      return t(`okr.monetary.title`)
    } else if (isDevelopment) {
      return t('okr.development.title')
    } else {
      return t('okr.suggestion.title')
    }
  }

  const hasToShowIconForCurrencyError = (objective: ObjectiveDto) => {
    return (
      !employeeView &&
      monetary &&
      objective.currency &&
      objective.currency !== employeeCurrentContractCurrency &&
      ![ObjectiveStatus.Dismissed, ObjectiveStatus.DismissedPending].includes(objective.status)
    )
  }

  const columns = [
    {
      key: ObjectiveColumnKey.Title,
      title: generateTitle(monetary, development),
      className: 'minWidth-column-xxl',
      render: (row: ObjectiveDto) => (
        <PerFlexRow align="middle">
          {hasToShowIconForCurrencyError(row) && (
            <Col span={3}>
              <IconIndicator
                icon="warning_fill"
                iconSize={12}
                background="error"
                size="l"
                tooltip={t('currencyConversion.tooltips.tableWarning')}
              />
            </Col>
          )}
          <Col span={hasToShowIconForCurrencyError(row) ? 20 : 23}>
            <PerText fontWeight="bold" color={row.status === ObjectiveStatus.DismissedPending ? 'secondary' : 'default'}>
              {monetary ? (
                <span data-e2e-monetary-okr-list-item-title={row.title}>{row.title}</span>
              ) : (
                <span data-e2e-development-okr-list-item-title={row.title}>{row.title}</span>
              )}
            </PerText>
          </Col>
        </PerFlexRow>
      ),
    },
    {
      key: ObjectiveColumnKey.Status,
      title: t('okr.objective.status.title'),
      className: 'textAlign-left minWidth-column-m',
      render: (row: ObjectiveDto) => {
        const statusTag = (color: string, text: string) => {
          return (
            <PerTag color={color} className="noMargin">
              {text}
            </PerTag>
          )
        }
        if (row.status === ObjectiveStatus.Proposal || row.status === ObjectiveStatus.ProposalFromManager) {
          return statusTag('cyan', t('okr.objective.status.proposal'))
        } else if (row.status === ObjectiveStatus.DismissedPending) {
          return statusTag('red', t('okr.objective.status.dismissedPending'))
        } else if (
          row.status === ObjectiveStatus.MarkedAsDone ||
          (row.status === ObjectiveStatus.Approved && row.achievedAmount !== null) ||
          row.status === ObjectiveStatus.PastApproved
        ) {
          return statusTag('gold', t('okr.objective.status.markedAsDone'))
        } else if (row.status === ObjectiveStatus.Approved) {
          return statusTag('lime', t('okr.objective.status.approved'))
        }
        return '' // fallback
      },
    },
    {
      key: ObjectiveColumnKey.MonetaryBonus,
      title: monetary && t('okr.objective.monetary.bonus'),
      dataIndex: 'targetAmount',
      className: 'textAlign-right minWidth-column-l',
      render: (targetAmount: number, objective: ObjectiveDto) => {
        const targetConversionRate: number | undefined =
          !!objective.currency && !!currencyConversionData && objective.currency !== employeeCurrentContractCurrency
            ? retrieveCurrencyConversionRate(objective.currency, employeeCurrentContractCurrency, currencyConversionData)
            : undefined
        return (
          targetAmount &&
          monetary && (
            <PerFlexRow direction="column">
              <Tooltip
                title={
                  objective.currency !== employeeCurrentContractCurrency
                    ? targetConversionRate !== undefined
                      ? employeeView
                        ? t('currencyConversion.tooltips.employeeTableWarning')
                        : t('currencyConversion.tooltips.tableWarning')
                      : employeeView
                      ? t('currencyConversion.tooltips.employeeTableConversionWarning', {
                          startingCurrency: objective.currency,
                          targetCurrency: employeeCurrentContractCurrency,
                        })
                      : t('currencyConversion.tooltips.tableConversionWarning', {
                          startingCurrency: objective.currency,
                          targetCurrency: employeeCurrentContractCurrency,
                        })
                    : undefined
                }
                placement="right"
              >
                <PerFlexRow>
                  <PerText
                    appearance={showToBePaidAndBonus ? 'label-small' : undefined}
                    strikeThrough={objective.status === ObjectiveStatus.DismissedPending}
                  >
                    {formatCurrency(targetAmount, objective.currency!)}
                  </PerText>
                </PerFlexRow>
                {objective.currency !== employeeCurrentContractCurrency && (
                  <PerFlexRow>
                    <PerText
                      appearance={showToBePaidAndBonus ? 'label-small' : undefined}
                      strikeThrough={objective.status === ObjectiveStatus.DismissedPending}
                      fontSize={showToBePaidAndBonus ? undefined : 'xs'}
                      mt="xxs"
                      color={targetConversionRate === undefined ? 'error' : undefined}
                      fontWeight={targetConversionRate === undefined ? 'bold' : undefined}
                    >
                      {calculateAndFormatNewTargetAmount(targetAmount, employeeCurrentContractCurrency, targetConversionRate)}
                    </PerText>
                  </PerFlexRow>
                )}
              </Tooltip>
            </PerFlexRow>
          )
        )
      },
    },
    {
      key: ObjectiveColumnKey.MonetaryPaid,
      title: monetary && t('okr.objective.monetary.toBePaid'),
      dataIndex: 'achievedAmount',
      className: 'textAlign-right minWidth-column-m',
      render: (achievedAmount: number, objective: ObjectiveDto) => {
        if (objective.status === ObjectiveStatus.DismissedPending) {
          return <PerText color="secondary">-</PerText>
        }
        const targetConversionRate: number | undefined =
          !!objective.currency && !!currencyConversionData && objective.currency !== employeeCurrentContractCurrency
            ? retrieveCurrencyConversionRate(objective.currency, employeeCurrentContractCurrency, currencyConversionData)
            : undefined
        return (
          achievedAmount !== null &&
          achievedAmount !== undefined &&
          monetary && (
            <PerFlexRow direction="column">
              <Tooltip
                title={
                  objective.currency !== employeeCurrentContractCurrency
                    ? targetConversionRate !== undefined
                      ? employeeView
                        ? t('currencyConversion.tooltips.employeeTableWarning')
                        : t('currencyConversion.tooltips.tableWarning')
                      : employeeView
                      ? t('currencyConversion.tooltips.employeeTableConversionWarning', {
                          startingCurrency: objective.currency,
                          targetCurrency: employeeCurrentContractCurrency,
                        })
                      : t('currencyConversion.tooltips.tableConversionWarning', {
                          startingCurrency: objective.currency,
                          targetCurrency: employeeCurrentContractCurrency,
                        })
                    : undefined
                }
                placement="right"
              >
                <PerText>{formatCurrency(achievedAmount, objective.currency || employeeCurrentContractCurrency)}</PerText>
                {objective.currency !== employeeCurrentContractCurrency && (
                  <PerText
                    mt="xxs"
                    fontWeight={targetConversionRate === undefined ? 'bold' : undefined}
                    fontSize="xs"
                    color={targetConversionRate === undefined ? 'error' : undefined}
                  >
                    {calculateAndFormatNewTargetAmount(achievedAmount, employeeCurrentContractCurrency, targetConversionRate)}
                  </PerText>
                )}
              </Tooltip>
            </PerFlexRow>
          )
        )
      },
    },
    {
      key: ObjectiveColumnKey.KeyResults,
      title: t('okr.objective.keyResults.tasks'),
      dataIndex: 'keyResults',
      className: 'textAlign-left minWidth-column-xl',
      render: (keyResults: KeyResultDto[], objective: ObjectiveDto) => {
        const keyResultsCompleted = keyResults.filter((kr) => kr.completed)
        const keyResultsLength = keyResults.length
        const keyResultsCompletedLength = keyResultsCompleted.length
        return (
          <>
            {!showTasks && (
              <PerText fontSize="s" color="secondary">
                <PerText
                  fontSize="xl"
                  display="inline"
                  color={
                    objective.status === ObjectiveStatus.DismissedPending
                      ? 'secondary'
                      : keyResultsLength === keyResultsCompletedLength
                      ? 'success'
                      : keyResultsCompletedLength === 0
                      ? 'error'
                      : 'warning'
                  }
                >
                  <span data-e2e-achieved-keyresults>{keyResultsCompletedLength}</span>
                </PerText>
                {' / '} <span data-e2e-total-keyresults>{keyResultsLength}</span>
              </PerText>
            )}
            {showTasks && generateKeyResults(keyResults)}
          </>
        )
      },
    },
    {
      key: ObjectiveColumnKey.Attachment,
      title: t('okr.objective.attachment'),
      className: 'textAlign-left minWidth-column-s',
      render: (objective: ObjectiveDto) => {
        return objective.attachment ? (
          <Popover
            key={objective.attachment}
            title={<PerText>{t('okr.objective.attachment')}</PerText>}
            content={<PerText className="text-ellipsis">{extractHumanReadableFileName(objective.attachment)}</PerText>}
          >
            <div>
              <PerIcon name="attachment_fill" size={16} />
            </div>
          </Popover>
        ) : null
      },
    },
    {
      key: ObjectiveColumnKey.Creator,
      title: t('okr.objective.creator'),
      className: 'textAlign-left minWidth-column-m',
      render: (row: ObjectiveDto) => {
        const statusTag = (color: string, text: string) => {
          return (
            <PerTag color={color} className="noMargin">
              {text}
            </PerTag>
          )
        }
        if (!!row.id || row.status === ObjectiveStatus.PastApproved) {
          return ''
        }
        if (row.status === ObjectiveStatus.Proposal) {
          return statusTag('volcano', t('okr.objective.status.creator.proposalFromEmployee'))
        }
        return statusTag('purple', t('okr.objective.status.creator.proposalFromManager'))
      },
    },
    {
      key: ObjectiveColumnKey.Start,
      title: t('okr.objective.start'),
      dataIndex: 'start',
      className: 'textAlign-left minWidth-column-m',
      render: (start: string, objective: ObjectiveDto) => (
        <PerText
          appearance={showStartDate ? 'label-small' : undefined}
          color={objective.status === ObjectiveStatus.DismissedPending ? 'secondary' : 'default'}
        >
          {formatDateShort(start, true)}
        </PerText>
      ),
    },
    {
      key: ObjectiveColumnKey.Due,
      title: t('okr.objective.due'),
      dataIndex: 'due',
      className: 'textAlign-left minWidth-column-m',
      render: (due: string, objective: ObjectiveDto) => (
        <PerText
          appearance={showStartDate ? 'label-small' : undefined}
          color={objective.status === ObjectiveStatus.DismissedPending ? 'secondary' : 'default'}
        >
          {formatDateShort(due, true)}
        </PerText>
      ),
    },
    {
      key: ObjectiveColumnKey.Actions,
      title: t('okr.objective.actions'),
      className: 'textAlign-left minWidth-column-m',
      dataIndex: 'status',
      width: tableActionButtonsRowWidth(3),
      render: (status: ObjectiveStatus, objective: ObjectiveDto) =>
        createObjectivesActionButtons(
          t,
          status,
          objective,
          employeeView,
          disabled,
          isDismissing,
          toggleDismissing,
          isReactivating,
          toggleReactivating,
          isDuplicating,
          toggleDuplicating,
          handleObjectiveClicked,
          async (objectiveId: number) => handleObjectiveDismiss && (await handleObjectiveDismiss(objectiveId)),
          async (objectiveId: number) => handleObjectiveReactivate && (await handleObjectiveReactivate(objectiveId)),
          async (objectiveGlobalId: string) => handleObjectiveDuplicate && (await handleObjectiveDuplicate(objectiveGlobalId)),
          canDuplicate,
          !!isManagerPreparation,
          employeeCurrentContractCurrency,
          handleObjectiveDelete,
          handleObjectiveDuplicateAndDismissOrDelete,
        ),
    },
  ]

  const filterStatusCol = (c: { key: string }) => showStatus || c.key !== ObjectiveColumnKey.Status
  const filterActionCol = (c: { key: string }) => !hideAction || c.key !== ObjectiveColumnKey.Actions
  const filterStartDateCol = (c: { key: string }) => showStartDate || c.key !== ObjectiveColumnKey.Start
  const filterToBePaidCol = (c: { key: string }) =>
    (monetary && toBePaid) || showToBePaidAndBonus || c.key !== ObjectiveColumnKey.MonetaryPaid

  return (
    <Table
      dataSource={objectives}
      rowKey={'globalId'}
      columns={columns.filter((c) => filterStatusCol(c) && filterActionCol(c) && filterStartDateCol(c) && filterToBePaidCol(c))}
      pagination={false}
      className={showToBePaidAndBonus ? `${!!fullWidth ? 'small fullWidth' : 'small'}` : !!fullWidth ? 'fullWidth' : undefined}
      locale={{
        emptyText: (
          <PerEmpty
            icon="targetsNo_stroke"
            heading={
              (listType === ObjectiveListType.Suggestion ? t('okr.suggestion.emptyTableTitle') : undefined) ||
              (listType === ObjectiveListType.Monetary ? t('okr.monetary.emptyTableTitle') : undefined) ||
              (listType === ObjectiveListType.PastMonetary
                ? t('okr.monetary.emptyTablePastTitle', { employee: employeeFirstName || '' })
                : undefined) ||
              (listType === ObjectiveListType.Development ? t('okr.development.emptyTableTitle') : undefined) ||
              (listType === ObjectiveListType.PastDevelopment
                ? t('okr.development.emptyTablePastTitle', { employee: employeeFirstName || '' })
                : undefined)
            }
            info={
              (listType === ObjectiveListType.Suggestion ? t('okr.suggestion.emptyTableText') : undefined) ||
              (listType === ObjectiveListType.Monetary ? t('okr.monetary.emptyTableText') : undefined) ||
              (listType === ObjectiveListType.PastMonetary ? t('okr.monetary.emptyTablePastText') : undefined) ||
              (listType === ObjectiveListType.Development ? t('okr.development.emptyTableText') : undefined) ||
              (listType === ObjectiveListType.PastDevelopment ? t('okr.development.emptyTablePastText') : undefined)
            }
            button={
              [ObjectiveListType.PastMonetary, ObjectiveListType.PastDevelopment].includes(listType) && (
                <PerButton
                  text={t('appraisalMeeting.buttons.newGoals')}
                  before="add_stroke"
                  color="success"
                  onClick={() => {
                    handleObjectiveClicked && handleObjectiveClicked(!!isManagerPreparation, undefined)
                  }}
                  data-e2e-add-more-targets-button
                />
              )
            }
          />
        ),
      }}
      data-e2e-objectives-list={listType}
    />
  )
}
